/**
 * Chapter003.js — OHSE & Environment Policy (OHSE 003)
 * Migrated from SWMS Platform Chapter003.tsx → JavaScript
 *
 * Props:
 *   data        {object}  ohse_policy from swmsData { policy_date, signed_by, signatory_title }
 *   onChange    {fn}      (newData) => void
 *   companyName {string}  from companyInfo.name
 */

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

const ACHIEVED_POINTS = [
  'complying with statutory requirements, codes, standards and guidelines;',
  'setting up objectives and targets with the aim of eliminating work related incidents in relation to our activities, products and services; and',
  'defining roles and responsibilities for occupational health, safety and environment.',
];

const STRATEGY_POINTS = [
  'ensuring occupational health, safety and environment management principles are included in all organisational planning activities;',
  'providing ongoing education and training to all our employees;',
  'consulting with employees and other parties to improve decision-making on occupational health, safety and environment matters;',
  'ensuring incidents are investigated and lessons are learnt within the organisation;',
  'distributing occupational health, safety and environment information, including this policy, to all employees and interested parties;',
  'providing enough resources to ensure occupational health, safety and environment is a central part of the organisation; and',
  'ensuring effective injury management and rehabilitation is provided to all employees.',
];

export default function Chapter003({ data = {}, onChange, companyName }) {
  const cn = companyName || '[Company Name]';
  const update = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 003</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">OHSE &amp; Environment Policy</h2>
      </div>

      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Policy Statement</h3>
          <p className="text-xs text-gray-500 mt-0.5">Company commitment to occupational health, safety and environment</p>
        </div>
        <div className="p-6 space-y-4 text-sm text-[#1a1a2e] leading-relaxed">
          <p>
            At <span className="font-semibold text-[#c9a84c]">{cn}</span> a commitment to occupational
            health, safety and the environment is part of the business.
          </p>

          <div>
            <p className="font-semibold mb-2">This is achieved through:</p>
            <ul className="space-y-1.5 ml-1">
              {ACHIEVED_POINTS.map((point, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#c9a84c] shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold mb-2">Strategies will include:</p>
            <ul className="space-y-1.5 ml-1">
              {STRATEGY_POINTS.map((point, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#c9a84c] shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Policy Authorisation */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">Policy Authorisation</h3>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Policy Date">
            <input type="date" value={data.policy_date || ''} onChange={e => update('policy_date', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Signed By">
            <input type="text" value={data.signed_by || ''} placeholder="Name of authorised signatory" onChange={e => update('signed_by', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Title / Position">
            <input type="text" value={data.signatory_title || ''} placeholder="Authorised person's title" onChange={e => update('signatory_title', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Signature (type name)">
            <input type="text" value={data.signature || ''} placeholder="Signature" onChange={e => update('signature', e.target.value)} className={inputCls} />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
